import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  Platform,
  View,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import PlayCircle from '@components/Icons/PlayCircle';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Document, LocalFile } from '@graphql/generated';
import theme from '@themes/theme';

export type FileGridCardState = 'uploaded' | 'uploading' | 'failed';

/**
 * Marks tiles showing the Shift-range *preview* tint (in range but not yet
 * selected), so E2E can tell a preview apart from a committed selection.
 */
export const RANGE_PREVIEW_TEST_ID = 'file-grid-card-range-preview';

export interface ProjectFileGridCardProps {
  item: Document | LocalFile;
  state: FileGridCardState;
  progress?: number; // For uploading state
  isSelectMode?: boolean;
  isSelected?: boolean;
  isInRange?: boolean; // For shift selection range preview
  cardWidth?: number; // Width of the card container
  onPress?: (
    item: Document | LocalFile,
    e?: any,
    forceSelection?: boolean
  ) => void;
  onLongPress?: (item: Document | LocalFile) => void;
  onRetry?: (item: Document | LocalFile) => void;
  onDelete?: (item: Document | LocalFile) => void;
}

const ProjectFileGridCard: React.FC<ProjectFileGridCardProps> = ({
  item,
  state,
  isSelected = false,
  isSelectMode = false,
  isInRange = false,
  cardWidth = 200,
  onPress,
  onLongPress,
  onRetry,
  onDelete,
}) => {
  const isDocument = (item: Document | LocalFile): item is Document => {
    return item.__typename === 'Document';
  };

  const [isHovered, setIsHovered] = useState(false);
  const onHoverIn = () => setIsHovered(true);
  const onHoverOut = () => setIsHovered(false);

  // Inject CSS to remove focus outline on radio button (only once)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const styleId = 'project-file-radio-button-no-outline';
      if (!window.document.getElementById(styleId)) {
        const style = window.document.createElement('style');
        style.id = styleId;
        style.textContent = `
          [data-radio-button-wrapper],
          [data-radio-button-wrapper] *,
          [data-radio-button-wrapper] button,
          [data-radio-button-wrapper] button:focus,
          [data-radio-button-wrapper] button:focus-visible,
          [data-radio-button-wrapper] button:active,
          [data-radio-button-wrapper] button:hover,
          [data-radio-button-wrapper] div[role="button"],
          [data-radio-button-wrapper] div[role="button"]:focus,
          [data-radio-button-wrapper] div[role="button"]:focus-visible,
          [data-radio-button-wrapper] div[role="button"]:active {
            outline: none !important;
            outline-offset: 0 !important;
            outline-width: 0 !important;
            box-shadow: none !important;
            -webkit-box-shadow: none !important;
            -moz-box-shadow: none !important;
            border: none !important;
          }
        `;
        window.document.head.appendChild(style);
      }
    }
  }, []);

  const mouseEvents =
    Platform.OS === 'web'
      ? { onMouseEnter: onHoverIn, onMouseLeave: onHoverOut }
      : {};

  const document = isDocument(item) ? item : null;
  const isImage = document?.isImage || item.isImage;
  const isVideo = document?.isVideo || item.isVideo;
  const isAudio = document?.isAudio || item.isAudio;
  const isPreviewable = isImage || isVideo;
  const contentType = document?.contentType || item.contentType;

  const owner = document?.owner;
  const createdAt = document?.createdAt || new Date().toISOString();
  const image = isDocument(item) ? item.file : item;

  // Determine if it's media (image, video, or audio)
  const isMedia = isImage || isVideo || isAudio;
  const accessibilityLabel = `${isMedia ? 'Media' : 'File'} | ${item.name}`;

  // Format date as "Oct 17, 2025"
  const formattedDate = dayjs(createdAt).format('MMM DD, YYYY');

  const handlePress = (e?: any) => {
    if (state === 'failed' || state === 'uploading') {
      return; // Don't allow preview when uploading or failed
    }
    onPress?.(item, e);
  };

  const handleRetry = (e: any) => {
    e.stopPropagation();
    onRetry?.(item);
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    onDelete?.(item);
  };

  const handleSelectableAction = (e?: any) => {
    if (state === 'failed' || state === 'uploading') {
      return; // Don't allow selection when uploading or failed
    }
    // When radio button is clicked, force selection mode
    if (e) {
      onPress?.(item, e, true); // forceSelection = true for radio button clicks
    } else {
      onLongPress?.(item);
    }
  };

  return (
    <View style={styles.card} {...mouseEvents}>
      <TouchableOpacity
        onPress={handlePress}
        onLongPress={handleSelectableAction}
        activeOpacity={state === 'uploaded' ? 0.7 : 1}
        style={styles.touchable}
        accessibilityLabel={accessibilityLabel}>
        <Box
          backgroundColor='white'
          borderRadius='xs'
          overflow='hidden'
          style={styles.cardContainer}>
          {/* Preview/Icon Area */}
          <Box
            borderRadius='xs'
            width='100%'
            aspectRatio={1}
            backgroundColor={
              (isHovered || isSelected || isInRange) && !isPreviewable
                ? 'grey02'
                : 'grey01'
            }
            borderWidth={isImage ? 0 : 1}
            borderColor={isImage ? 'transparent' : 'grey02'}
            position='relative'
            justifyContent='center'
            alignItems='center'>
            {state === 'uploaded' && (
              <>
                {isImage ? (
                  <>
                    <Box
                      borderRadius='xs'
                      style={[
                        StyleSheet.absoluteFillObject,
                        { overflow: 'hidden' },
                      ]}>
                      <CachedImage
                        imageClientId={item.clientId}
                        image={image}
                        width={Math.ceil(cardWidth)}
                        height={Math.ceil(cardWidth)}
                        contentType={contentType}
                        resizeMode='cover'
                      />
                    </Box>
                    {isVideo && (
                      <Box borderRadius='xs' style={styles.playIconContainer}>
                        <PlayCircle />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    width='100%'
                    justifyContent='center'
                    alignItems='center'
                    maxWidth={cardWidth}>
                    <Icon
                      fileType={contentType || undefined}
                      color='blue'
                      variant='xl'
                      backgroundColor='transparent'
                    />

                    <Text
                      textAlign='center'
                      paddingHorizontal='xs'
                      maxWidth={cardWidth}
                      variant='metadataSecondary'
                      color='textPrimary'>
                      {item.name}
                    </Text>
                  </Box>
                )}

                {/* Overlay - show when selected, hovered, or in range */}
                {(isHovered || isSelected || isInRange) &&
                  isPreviewable &&
                  isImage && (
                    <Box
                      style={styles.overlay}
                      backgroundColor='black'
                      opacity={isInRange && !isSelected ? 0.15 : 0.25}
                      testID={
                        isInRange && !isSelected
                          ? RANGE_PREVIEW_TEST_ID
                          : undefined
                      }
                    />
                  )}

                {/* Radio button - show when select mode is enabled, card is hovered, or in range */}
                {(isSelectMode || isHovered || isInRange) && (
                  <Box style={styles.checkboxContainer}>
                    <View
                      style={
                        Platform.OS === 'web'
                          ? styles.radioButtonWrapper
                          : undefined
                      }
                      // @ts-ignore - web-only data attribute and props
                      {...(Platform.OS === 'web' && {
                        'data-radio-button-wrapper': true,
                        tabIndex: -1,
                        onMouseDown: (e: any) => {
                          // Prevent focus on mouse down
                          if (e.target) {
                            e.target.blur?.();
                            e.preventDefault?.();
                          }
                        },
                        onFocus: (e: any) => {
                          // Blur immediately if focused
                          if (e.target) {
                            e.target.blur?.();
                          }
                        },
                      })}>
                      <RadioButton
                        iconVariant='m'
                        backgroundColor='#D9D9D980'
                        stateIconColor='white'
                        isSelected={isSelected || isInRange}
                        onPress={handleSelectableAction}
                      />
                    </View>
                  </Box>
                )}
              </>
            )}

            {state === 'uploading' && (
              <Box style={styles.loadingContainer}>
                <ActivityIndicator
                  size='small'
                  color={theme.colors.textPrimary}
                />
              </Box>
            )}

            {state === 'failed' && (
              <Box style={styles.failedContainer}>
                <Text
                  variant='labelTabHeaderText'
                  color='textPrimary'
                  marginBottom='s'>
                  Upload Failed
                </Text>
                <Box flexDirection='row' alignItems='center' gap='s'>
                  <TouchableOpacity
                    onPress={handleRetry}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                    <Icon name='RotateCcw' variant='s' color='grey05' />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleDelete}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                    <Icon name='Trash2' variant='s' color='grey05' />
                  </TouchableOpacity>
                </Box>
              </Box>
            )}
          </Box>

          {/* Uploader Info */}
          {owner && (
            <Box
              marginTop='xxs'
              flexDirection='row'
              alignItems='center'
              gap='xs'>
              <Avatar
                id={owner.id}
                avatar={owner.avatar}
                size='xs'
                label={owner.name || undefined}
              />
              <Box flex={1}>
                <Text variant='metadata' color='textPrimary' numberOfLines={1}>
                  {owner.name || 'Unknown'}
                </Text>
                <Text variant='metadata' color='grey04'>
                  {formattedDate}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </TouchableOpacity>
    </View>
  );
};

export default ProjectFileGridCard;

const styles = StyleSheet.create({
  card: {
    width: '100%',
  },
  touchable: {
    // @ts-ignore - web-only CSS properties
    outlineStyle: 'none',
    boxShadow: 'none',
  },
  cardContainer: {
    width: '100%',
    borderColor: theme.colors.grey02,
  },
  checkboxContainer: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 10,
  },
  radioButtonWrapper: {
    // @ts-ignore - web-only CSS properties to remove focus outline
    outline: 'none',
    outlineStyle: 'none' as any,
    outlineOffset: 0,
    boxShadow: 'none',
    // @ts-ignore
    WebkitBoxShadow: 'none',
    // @ts-ignore
    MozBoxShadow: 'none',
  },
  playIconContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  failedContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.s,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: theme.borderRadii.xs,
    // No opacity here on purpose: Restyle composes as [restyleStyle, props.style],
    // so a value set here would override the `opacity` prop and flatten the
    // lighter in-range preview tint into the committed-selection tint (TA-10163).
  },
});
